import React, { useState, useEffect, useMemo, use } from "react";
import dayjs from "dayjs";
import Modal from "../../Modal/Modal";
import TableAdv from "../../Table/TableAdv/TableAdv";
import SmartTooltip from "../../Info/SmartTooltip";
import PageTab from "../../DynamicTabs/PageTab";
import Backdrop from "../../Backgrounds/Backdrop";
import LogoLoader from "../../Loading/LogoLoader";
import { toTitleCase } from "../../../utils/common";
import { formatWords2 } from "../../../utils/stringUtils";

export default function InternalSubmissionModal({
  isOpen,
  onClose,
  data,
  employeeId,
  stageName = "Internal Submission",
  onStageNumberChange,
  totalCount,
  page,
  limit,
  setPage,
  handleLimitChange,
  tab2Data,
  tab3Data,
  tab2TotalCount,
  tab3TotalCount,
  loading
}) {
  const [activeTab, setActiveTab] = useState(0);
  useEffect(() => {
    onStageNumberChange?.(activeTab);
  }, [activeTab]);
const mapTreeHierarchyData = (apiResponse) => {
  const jobs = {};

  (apiResponse || []).forEach((job) => {
    const jobId = job.uiid;

    if (!jobs[jobId]) {
      jobs[jobId] = {
        jobId,
        jobTitle: job.jobTitle,
        companyName: job.companyName,
        jobOpenDate: job.addedOn,
        bdmId: job?.addedBy?.uiid,
        submissions: [],
      };
    }

    const subs = job.submissions || [];

    subs.forEach((sub) => {
      jobs[jobId].submissions.push({
        submissionId: sub.uiid,
        candidateName: sub.candidateName,
        status: sub?.statusHistory?.status,
        submittedDate: sub?.statusHistory?.addedOn,
      });
    });
  });

  return { jobs };
};


  const mappedData = useMemo(() => mapTreeHierarchyData(data), [data]);

  const employeeJobs = useMemo(() => {
    const jobs =
      mappedData?.jobs && typeof mappedData.jobs === "object"
        ? mappedData.jobs
        : {};

    return Object.values(jobs)
      .filter(Boolean)
      .filter(
        (job) => String(job?.bdmId ?? "") === String(employeeId ?? "")
      );
  }, [mappedData, employeeId]);

  // Build candidate rows
  const candidateRows = useMemo(() => {
    const rows = [];
    employeeJobs.forEach((job) => {
      job.submissions?.forEach((sub) => {
        if (!sub?.status) return;

        rows.push({
          candidateName: toTitleCase(sub?.candidateName) ?? "",
          submittedDate: sub?.submittedDate
            ? dayjs(sub.submittedDate).format("MMM DD, YYYY")
            : "",
          jobTitle: toTitleCase(job?.jobTitle) ?? "",
          companyName: toTitleCase(job?.companyName) ?? "",
        });
      });
    });

    return rows;
  }, [employeeJobs]);

  // Count submissions
  const getJobSubmissionCount = (job) => {
    return job?.submissions?.length || 0;
  };

  const jobsWith = useMemo(() => {
    if (!Array.isArray(tab2Data)) return [];

    return (tab2Data || []).map((item) => ({
      jobTitle: toTitleCase(item?.jobTitle) ?? "-",
      companyName: toTitleCase(item?.companyName) ?? "-",
      totalSubmissions: item?.totalSubmissions ?? 0,
    }));
  }, [tab2Data]);

  const jobsWithout = useMemo(() => {
    if (!Array.isArray(tab2Data)) return [];

    return (tab3Data || []).map((item) => ({
      jobTitle: toTitleCase(item?.jobTitle) ?? "-",
      companyName: toTitleCase(item?.companyName) ?? "-",
    }));
  }, [tab3Data]);

  const candidateColumns = useMemo(
    () => [
      { key: "candidateName", label: "Candidate Name" },
      {
        key: "submittedDate",
        label: "Submitted Date",
      },
      {
        key: "jobTitle",
        label: "Job Title",
        render: (row) => (
          <SmartTooltip description={formatWords2(row?.jobTitle)} noicon>
            <div className="line-clamp-1">{formatWords2(row?.jobTitle)}</div>
          </SmartTooltip>
        ),
      },
      {
        key: "companyName",
        label: "Company",
        render: (row) => (
          <SmartTooltip description={formatWords2(row?.companyName)} noicon>
            <div className="line-clamp-1">{formatWords2(row?.companyName)}</div>
          </SmartTooltip>
        ),
      },
    ],
    []
  );

  const jobWithColumns = useMemo(
    () => [
      { key: "jobTitle", label: "Job Title" },
      { key: "companyName", label: "Company" },
      { key: "totalSubmissions", label: "Total Submissions" },
    ],
    []
  );
  const totalAddedCount = useMemo(() => {
    if (!data || !totalCount) return 0;

    let total = 0;

    data.forEach(item => {
      const bdmId = item?.addedBy?.uiid;

      const match = totalCount.find(
        t => String(t?.addedBy?.uiid) === String(bdmId)
      );

      if (match) {
        total = Number(match?.submissions?.internalSubmissions || 0);
      }
    });

    return total;
  }, [data, totalCount]);
  const jobWithoutColumns = useMemo(
    () => [
      { key: "jobTitle", label: "Job Title" },
      { key: "companyName", label: "Company" },
    ],
    []
  );

  const tabs = useMemo(
    () => [
      {
        title: `Internal Submissions (${totalAddedCount})`,
        type: "main",
        page: (
          <div className="h-full overflow-hidden flex flex-col p-0.5">
            <TableAdv
              showSlNo
              columns={candidateColumns}
              rows={candidateRows}
              serverPagination={true}
              totalCount={totalAddedCount}
              page={page}
              limit={limit}
              onPaginationChange={setPage}
              onLimitChange={handleLimitChange}
              tableId="treeHierarchyInternalCandidates"
              toolbarRight={{
                search: true,
                filter: false,
                download: false,
                viewToggle: false,
                fullScreen: false,
                add: [],
              }}
              toolbarLeft={{ add: [] }}
            />
          </div>
        ),
      },
      {
        title: `Jobs with Internal Submission (${tab2TotalCount ?? 0})`,
        type: "main",
        page: (
          <div className="h-full overflow-hidden flex flex-col p-0.5">
            <TableAdv
              columns={jobWithColumns}
              rows={jobsWith}
              totalCount={tab2TotalCount ?? 0}
              serverPagination={true}
              page={page}
              limit={limit}
              onPaginationChange={setPage}
              onLimitChange={handleLimitChange}
              tableId="treeHierarchyInternalJobsWith"
              toolbarRight={{
                search: true,
                filter: false,
                download: false,
                viewToggle: false,
                fullScreen: false,
                add: [],
              }}
              toolbarLeft={{ add: [] }}
            />
          </div>
        ),
      },
      {
        title: `Jobs without Internal Submission (${tab3TotalCount ?? 0})`,
        type: "main",
        page: (
          <div className="h-full overflow-hidden flex flex-col p-0.5">
            <TableAdv
              columns={jobWithoutColumns}
              rows={jobsWithout}
              
              serverPagination={true}
              totalCount={tab3TotalCount ?? 0}
              page={page}
              limit={limit}
              onPaginationChange={setPage}
              onLimitChange={handleLimitChange}
              tableId="treeHierarchyInternalJobsWithout"
              toolbarRight={{
                search: true,
                filter: false,
                download: false,
                viewToggle: false,
                fullScreen: false,
                add: [],
              }}
              toolbarLeft={{ add: [] }}
            />
          </div>
        ),
      },
    ],
    [
      candidateColumns,
      candidateRows,
      jobWithColumns,
      jobWithoutColumns,
      jobsWith,
      jobsWithout,
      page,
      limit,
      setPage,
      handleLimitChange
    ]
  );


  return (

    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setActiveTab(0);
      }}
      title="Internal Submission"
      width="max-w-6xl"
      bodyClassName="p-0 flex flex-1 overflow-hidden"
    >
      <div className="w-full max-h-[70vh] overflow-hidden p-2 flex flex-1 ">
        <PageTab
          tabs={tabs}
          defaultTab={0}
          variant="text"
          activeVariant="contained"
          color="black"
          activeColor="primary"
          onTabChange={(index) => setActiveTab(index)}
        />
      </div>
         <Backdrop isOpen={loading} zIndex={53}>
        <LogoLoader />
      </Backdrop>
    </Modal>
  );
}
